import cx from 'classnames'
import { Button } from 'components/Button/Button'
import { Helmet } from 'react-helmet'
import CompanyLogo from 'components/CompanyLogo/CompanyLogo'
import { ButtonLink } from 'components/Button/ButtonLink'
import { useHandbook } from 'components/Handbook2_0/hooks'
import { SwitchAccount } from 'components/SwitchAccount/SwitchAccount'
import { useRequestReadAccess } from 'hooks/queries/articles/useRequestReadAccess'
import { useSession } from 'hooks/useSession'
import { useBrand } from 'hooks/useBrand'
import { HOME_PATH, LOGIN_OR_SIGNUP_PATH, LOGIN_PATH, RECENT_DOCS_PATH } from 'paths'
import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { generatePath, Redirect, RouteComponentProps, withRouter } from 'react-router-dom'
import { RootState } from 'types'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import Styles from './RequestAccess.module.scss'

interface Props extends RouteComponentProps {
  auth?: RootState['auth']
  articleId: string
  type: 'doc' | 'handbook' | 'folder' | 'design'
  workspaceId: number | null
}

const pageContents = {
  handbook: {
    infoText: 'Request access to this handbook or switch to an account with access.',
    requestButtonText: 'Request Access to Handbook',
  },
  doc: {
    infoText: 'Request access to this document or switch into an account with access.',
    requestButtonText: 'Request Access to Document',
  },
  folder: {
    infoText: 'Request access to this folder or switch into an account with access.',
    requestButtonText: 'Request Access to Folder',
  },
  design: {
    infoText: 'Request access to this design or switch into an account with access.',
    requestButtonText: 'Request Access to Design',
  },
}

export const RequestAccess = ({ auth, articleId, type, location, workspaceId }: Props) => {
  const [requestReadAccess, requestingAccess] = useRequestReadAccess()
  const { logout } = useSession()
  const handbook = useHandbook()
  const inHandbook = !!handbook
  const { name: brandName } = useBrand()
  const [filesPath, setFilesPath] = useState(HOME_PATH)

  useEffect(() => {
    if (auth)
      trackEventInHeap(HeapEventType.AccessBlocked, {
        action: type === 'handbook' ? 'handbook_access_blocked' : 'doc_access_blocked',
        type: '',
      })
  }, [auth, type])

  useEffect(() => {
    const currentWorkspaceId = workspaceId || auth?.company_id
    if (!currentWorkspaceId) return

    if (currentWorkspaceId === ALMANAC_CORE_WORKSPACE_ID) {
      setFilesPath(HOME_PATH)
      return
    }

    setFilesPath(generatePath(RECENT_DOCS_PATH, { workspaceId: currentWorkspaceId }))
  }, [auth, workspaceId])

  if (!auth)
    return <Redirect to={`${LOGIN_OR_SIGNUP_PATH}?redirect_to=${location.pathname}&${location.search.substring(1)}`} />

  const onSwitchAccount = () => {
    logout({ logoutRedirect: `${LOGIN_PATH}?redirect_to=${location.pathname}&${location.search.substring(1)}` })
  }

  const requestAccess = () => {
    requestReadAccess(articleId)
  }

  return (
    <>
      <Helmet>
        <title>{brandName}</title>
      </Helmet>
      <div className={cx(Styles.container, { [Styles.inHandbook]: inHandbook })}>
        <div className={Styles.logoContainer}>
          <CompanyLogo />
        </div>
        <div className={Styles.permissionRequestCard}>
          <div className={Styles.title}>You need permission</div>
          <p className={Styles.textInfo}>{pageContents[type]['infoText']}</p>
          <div className={Styles.buttonsWrapper}>
            <Button isDisabled={requestingAccess} size="xl" variant="primary" onPress={requestAccess}>
              <span className={Styles.buttonText}>{pageContents[type]['requestButtonText']}</span>
            </Button>
            <ButtonLink data-testid="all-files-button" size="xl" variant="tertiary" to={filesPath}>
              <span className={Styles.buttonText}>View recent files</span>
            </ButtonLink>
          </div>
          <div className={Styles.switchAccountWrapper}>
            <SwitchAccount
              avatarURL={auth.avatar}
              email={auth.email}
              displayName={auth.display_name}
              onSwitchAccount={onSwitchAccount}
              isDisabled={requestingAccess}
            />
          </div>
        </div>
      </div>
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return { auth: state.auth }
}

export default withRouter(connect(mapStateToProps, null)(RequestAccess))
